/**
 * InfoOverlay Component - Pokemon stats overlay on hover
 */

import type { Pokemon } from '../types/pokemon';
import { getBuildForPokemon } from '../data/smogonSets';
import { useApp } from '../context/AppContext';

interface InfoOverlayProps {
  pokemon: Pokemon;
}

export function InfoOverlay({ pokemon }: InfoOverlayProps) {
  const { state } = useApp();
  const build = getBuildForPokemon(pokemon, state.settings.tier);

  return (
    <div
      className="info-overlay absolute inset-0 p-6 opacity-0 group-hover:opacity-100 transition-all duration-300 flex flex-col justify-center text-white pointer-events-none z-20"
      style={{
        background: 'linear-gradient(135deg, rgba(15, 23, 42, 0.96), rgba(30, 41, 59, 0.96))',
        backdropFilter: 'blur(20px) saturate(180%)',
        WebkitBackdropFilter: 'blur(20px) saturate(180%)',
        borderColor: 'rgba(0, 0, 0, 0.7)',
        borderWidth: '4px',
        borderRadius: '16px',
        boxShadow: `inset 0 2px 8px rgba(0, 0, 0, 0.5), inset 0 -2px 8px rgba(255, 255, 255, 0.1), 0 0 40px rgba(59, 130, 200, 0.4)`
      }}
    >
      {/* Title */}
      <h3 className="text-base font-black mb-2 text-center text-yellow-DEFAULT uppercase tracking-wider text-shadow-retro">
        STATS
      </h3>

      {/* Stats List */}
      <div className="space-y-1 mb-2">
        {pokemon.stats.map((stat) => (
          <div key={stat.name} className="flex items-center gap-2 text-sm">
            <span className="font-semibold w-20 flex-shrink-0">{translateStat(stat.name)}</span>
            <div className="flex items-center gap-2 flex-1">
              <div className="flex-1 bg-poke-blue-darker rounded h-3 overflow-hidden border-2 border-white/30">
                <div
                  className="h-full bg-gradient-to-r from-yellow-400 via-yellow-300 to-green-400 transition-all duration-500"
                  style={{
                    width: `${Math.min(stat.value / 255 * 100, 100)}%`,
                    boxShadow: 'inset 0 2px 4px rgba(255, 255, 255, 0.5)'
                  }}
                />
              </div>
              <span className="text-xs font-bold text-yellow-300 w-8 text-right flex-shrink-0">
                {stat.value}
              </span>
            </div>
          </div>
        ))}
      </div>

      {/* Physical Info */}
      <div className="grid grid-cols-2 gap-2 text-xs border-t-2 border-yellow-400/50 pt-2 mb-2">
        <div className="text-center bg-gray-800/50 rounded px-2 py-1">
          <div className="text-yellow-400 font-bold">TAILLE</div>
          <div className="font-bold text-white">{(pokemon.height / 10).toFixed(1)} m</div>
        </div>
        <div className="text-center bg-gray-800/50 rounded px-2 py-1">
          <div className="text-yellow-400 font-bold">POIDS</div>
          <div className="font-bold text-white">{(pokemon.weight / 10).toFixed(1)} kg</div>
        </div>
      </div>

      {/* Additional Info */}
      <div className="space-y-1 text-xs mb-2">
        <div className="flex items-center justify-between bg-gray-800/50 rounded px-2 py-1">
          <span className="text-yellow-400 font-bold">CATÉGORIE</span>
          <span className="text-white font-bold">{pokemon.category}</span>
        </div>
        <div className="flex items-center justify-between bg-gray-800/50 rounded px-2 py-1">
          <span className="text-yellow-400 font-bold">GÉNÉRATION</span>
          <span className="text-white font-bold">GEN {pokemon.generation}</span>
        </div>
        <div className="flex items-center justify-between bg-gray-800/50 rounded px-2 py-1">
          <span className="text-yellow-400 font-bold">GROUPE ŒUFS</span>
          <span className="text-white font-bold">{pokemon.eggGroups.join(' • ')}</span>
        </div>
      </div>

      {/* Build Section */}
      <div className="border-t-2 border-yellow-400/50 pt-2">
        <div className="text-yellow-400 font-bold text-xs mb-1 uppercase tracking-wider">Build ({state.settings.tier.toUpperCase()})</div>
        <div className="space-y-0.5 text-xs">
          <div className="flex items-center justify-between bg-gray-800/50 rounded px-2 py-0.5">
            <span className="text-gray-400">Objet</span>
            <span className="text-white font-semibold">{build.item}</span>
          </div>
          <div className="flex items-center justify-between bg-gray-800/50 rounded px-2 py-0.5">
            <span className="text-gray-400">Talent</span>
            <span className="text-white font-semibold">{build.ability}</span>
          </div>
          <div className="flex items-center justify-between bg-gray-800/50 rounded px-2 py-0.5">
            <span className="text-gray-400">Nature</span>
            <span className="text-white font-semibold">{build.nature}</span>
          </div>
          <div className="bg-gray-800/50 rounded px-2 py-0.5">
            <div className="text-gray-400 mb-0.5">Moves</div>
            <div className="grid grid-cols-2 gap-x-2">
              {build.moves.map((move, i) => (
                <span key={i} className="text-white font-semibold truncate">{move}</span>
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

function translateStat(statName: string): string {
  const translations: Record<string, string> = {
    'hp': 'PV',
    'attack': 'Attaque',
    'defense': 'Défense',
    'special-attack': 'Atq. Spé.',
    'special-defense': 'Déf. Spé.',
    'speed': 'Vitesse'
  };
  return translations[statName] || statName;
}

export default InfoOverlay;
